import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Organization Recordings - Recording library
 *
 * Features:
 * - Grid/List view toggle
 * - Recording cards with title, host, date, duration
 * - Play, Download, Delete actions
 * - Storage indicator
 */

export default function OrgRecordings({ organization, members, stats, isAdmin }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (organization?.id) {
      loadRecordings();
    }
  }, [organization?.id]);

  const loadRecordings = async () => {
    try {
      setLoading(true);
      const recordingsRef = collection(db, `organizations/${organization.id}/recordings`);
      const q = query(recordingsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const recordingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setRecordings(recordingsData);
    } catch (error) {
      console.error('Error loading recordings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recordingId) => {
    if (!confirm('Delete this recording? This action cannot be undone.')) return;

    try {
      await deleteDoc(doc(db, `organizations/${organization.id}/recordings/${recordingId}`));
      setRecordings(recordings.filter(r => r.id !== recordingId));
    } catch (error) {
      console.error('Error deleting recording:', error);
    }
  };

  const filteredRecordings = recordings.filter(r =>
    r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.meetingTitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getMemberEmail = (userId) => {
    const member = members.find(m => m.userId === userId || m.id === userId);
    return member?.email?.split('@')[0] || 'Unknown';
  };

  const storagePercentage = ((stats?.storageUsed || 0) / (stats?.storageLimit || 1000)) * 100;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Recordings</h2>
          <p className="text-white/50 text-sm mt-1">{recordings.length} recordings</p>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Storage Indicator */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white/70 text-sm">Storage Used</span>
          <span className="text-white font-medium">{stats?.storageUsed || 0} GB / {stats?.storageLimit || 1000} GB</span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              storagePercentage > 90 ? 'bg-gradient-to-r from-red-500 to-orange-500' :
              storagePercentage > 70 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
              'bg-gradient-to-r from-[#06c] to-purple-500'
            }`}
            style={{ width: `${Math.min(storagePercentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search recordings..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#06c]/50"
        />
      </div>

      {/* Recordings */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-white/20 border-t-[#06c] rounded-full animate-spin" />
        </div>
      ) : filteredRecordings.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRecordings.map(recording => (
              <div
                key={recording.id}
                className="group backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 overflow-hidden hover:border-white/25 transition-all"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-gradient-to-br from-[#06c]/20 to-purple-500/20 flex items-center justify-center">
                  <svg className="w-12 h-12 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 rounded text-white text-xs">
                    {formatDuration(recording.duration)}
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="text-white font-medium text-sm truncate">{recording.title || recording.meetingTitle || 'Untitled Recording'}</h3>
                  <p className="text-white/50 text-xs mt-1">{getMemberEmail(recording.createdBy)} • {formatDate(recording.createdAt)}</p>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <button className="flex-1 px-3 py-2 bg-[#06c]/20 text-[#06c] rounded-lg text-xs font-medium hover:bg-[#06c]/30 transition-colors">
                      Play
                    </button>
                    <button className="flex-1 px-3 py-2 bg-white/10 text-white/70 rounded-lg text-xs font-medium hover:bg-white/20 transition-colors">
                      Download
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(recording.id)}
                        className="px-3 py-2 bg-red-500/10 text-red-400 rounded-lg text-xs hover:bg-red-500/20 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 divide-y divide-white/5">
            {filteredRecordings.map(recording => (
              <div key={recording.id} className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors">
                <div className="w-16 h-12 rounded-lg bg-gradient-to-br from-[#06c]/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium text-sm truncate">{recording.title || 'Untitled'}</h3>
                  <p className="text-white/50 text-xs">{getMemberEmail(recording.createdBy)} • {formatDate(recording.createdAt)} • {formatDuration(recording.duration)}</p>
                </div>
                <div className="flex gap-2">
                  <button className="px-3 py-1.5 bg-[#06c]/20 text-[#06c] rounded-lg text-xs font-medium hover:bg-[#06c]/30">Play</button>
                  <button className="px-3 py-1.5 bg-white/10 text-white/70 rounded-lg text-xs hover:bg-white/20">Download</button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-16 backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15">
          <div className="text-5xl mb-4">🎥</div>
          <div className="text-white/50">No recordings yet</div>
          <div className="text-white/30 text-sm mt-1">Recordings will appear here when your team records meetings</div>
        </div>
      )}
    </div>
  );
}
